/**
 * OrganizationTab — extracted from EmployeeCreate.js.
 * Receives state + handlers via `ctx` prop bag.
 */
import React from 'react';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Button } from '../../../../components/ui/button';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Textarea } from '../../../../components/ui/textarea';
import { TabsContent } from '../../../../components/ui/tabs';
import { Badge } from '../../../../components/ui/badge';
import { Plus, Trash2, User, Building2, Briefcase, Mail, Phone, Home, CreditCard, FileText, GraduationCap, Clock, DollarSign, Users, Heart, Calendar, Wallet, RefreshCw } from 'lucide-react';

const OrganizationTab = ({ ctx }) => {
  const {
    formData,
    companyCodes,
    costCenters,
    personnelAreas,
    personnelSubareas,
    employeeGroups,
    employeeSubgroups,
    handleOrgChange,
  } = ctx;

  return (
          <TabsContent value="organization">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-stone-700" />
                  Organizational Assignment (Infotype 0001)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Company Code *</Label>
                    <select
                      value={formData.organizational_assignment.company_code}
                      onChange={(e) => handleOrgChange('company_code', e.target.value)}
                      className="w-full h-10 px-3 border rounded-md"
                      data-testid="company-code-select"
                    >
                      <option value="">Select Company</option>
                      {companyCodes.map(cc => (
                        <option key={cc.company_code_id} value={cc.company_code_id}>
                          {cc.company_code_id} - {cc.company_code_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Personnel Area</Label>
                    <select
                      value={formData.organizational_assignment.personnel_area}
                      onChange={(e) => handleOrgChange('personnel_area', e.target.value)}
                      className="w-full h-10 px-3 border rounded-md"
                      data-testid="personnel-area-select"
                    >
                      <option value="">Select Personnel Area</option>
                      {personnelAreas.map(pa => (
                        <option key={pa.personnel_area_id} value={pa.personnel_area_id}>
                          {pa.personnel_area_id} - {pa.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Personnel Subarea</Label>
                    <select
                      value={formData.organizational_assignment.personnel_subarea}
                      onChange={(e) => handleOrgChange('personnel_subarea', e.target.value)}
                      className="w-full h-10 px-3 border rounded-md"
                      data-testid="personnel-subarea-select"
                    >
                      <option value="">Select Personnel Subarea</option>
                      {personnelSubareas
                        .filter(psa => !formData.organizational_assignment.personnel_area || 
                          psa.personnel_area_id === formData.organizational_assignment.personnel_area)
                        .map(psa => (
                          <option key={`${psa.personnel_area_id}-${psa.personnel_sub_area_id}`} value={psa.personnel_sub_area_id}>
                            {psa.personnel_sub_area_id} - {psa.name}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <Label>Business Area</Label>
                    <Input
                      value={formData.organizational_assignment.business_area}
                      onChange={(e) => handleOrgChange('business_area', e.target.value)}
                      placeholder="BA01"
                    />
                  </div>
                  <div>
                    <Label>Organizational Unit (Department)</Label>
                    <Input
                      value={formData.organizational_assignment.organizational_unit}
                      onChange={(e) => handleOrgChange('organizational_unit', e.target.value)}
                      placeholder="IT Department"
                    />
                  </div>
                  <div>
                    <Label>Position</Label>
                    <Input
                      value={formData.organizational_assignment.position}
                      onChange={(e) => handleOrgChange('position', e.target.value)}
                      placeholder="Software Engineer"
                    />
                  </div>
                  <div>
                    <Label>Job Title</Label>
                    <Input
                      value={formData.organizational_assignment.job}
                      onChange={(e) => handleOrgChange('job', e.target.value)}
                      placeholder="Senior Developer"
                    />
                  </div>
                  <div>
                    <Label>Cost Center</Label>
                    <select
                      value={formData.organizational_assignment.cost_center}
                      onChange={(e) => handleOrgChange('cost_center', e.target.value)}
                      className="w-full h-10 px-3 border rounded-md"
                    >
                      <option value="">Select Cost Center</option>
                      {costCenters.map(cc => (
                        <option key={cc.cost_center_id} value={cc.cost_center_id}>
                          {cc.cost_center_id} - {cc.description}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Employee Group</Label>
                    <select
                      value={formData.organizational_assignment.employee_group}
                      onChange={(e) => handleOrgChange('employee_group', e.target.value)}
                      className="w-full h-10 px-3 border rounded-md"
                      data-testid="employee-group-select"
                    >
                      <option value="">Select Employee Group</option>
                      {employeeGroups.map(eg => (
                        <option key={eg.employee_group_id} value={eg.employee_group_id}>
                          {eg.employee_group_id} - {eg.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Employee Subgroup</Label>
                    <select
                      value={formData.organizational_assignment.employee_subgroup}
                      onChange={(e) => handleOrgChange('employee_subgroup', e.target.value)}
                      className="w-full h-10 px-3 border rounded-md"
                      data-testid="employee-subgroup-select"
                    >
                      <option value="">Select Employee Subgroup</option>
                      {employeeSubgroups
                        .filter(esg => !formData.organizational_assignment.employee_group || 
                          esg.employee_group_id === formData.organizational_assignment.employee_group)
                        .map(esg => (
                          <option key={`${esg.employee_group_id}-${esg.employee_subgroup_id}`} value={esg.employee_subgroup_id}>
                            {esg.employee_subgroup_id} - {esg.name}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <Label>Payroll Area</Label>
                    <Input
                      value={formData.organizational_assignment.payroll_area}
                      onChange={(e) => handleOrgChange('payroll_area', e.target.value)}
                      placeholder="IN01"
                    />
                  </div>
                  <div>
                    <Label>Supervisor ID</Label>
                    <Input
                      value={formData.organizational_assignment.supervisor_id}
                      onChange={(e) => handleOrgChange('supervisor_id', e.target.value)}
                      placeholder="EMP000001"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
  );
};

export default OrganizationTab;
